import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Flame } from 'lucide-react';
import api from '../lib/api';

const GREETING = { role: 'assistant', content: "Hi! I'm the Closefire Assistant. How can I help you today?" };

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const history = next
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));
      const { data } = await api.post('/chat', { message: text, history });
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: (data && data.reply) || "Sorry, I couldn't get a response. Please try again or contact us." },
      ]);
    } catch (err) {
      const reply =
        err?.response?.data?.reply ||
        "Sorry, I couldn't reach the assistant. Please try again, or contact Closefire at closefireindia@gmail.com.";
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  return (
    <>
      {/* Floating chat button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-fab"
        aria-label={open ? 'Close chat assistant' : 'Open chat assistant'}
        className="fixed bottom-24 right-6 z-[80] grid place-items-center w-14 h-14 rounded-full bg-gradient-to-r from-primary to-accent text-white shadow-xl shadow-primary/40 hover:opacity-95 transition-opacity"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ type: 'spring', damping: 26, stiffness: 300 }}
            className="fixed z-[95] bottom-6 right-4 left-4 sm:left-auto sm:right-6 sm:w-[380px] h-[70dvh] sm:h-[560px] max-h-[calc(100dvh-2rem)] flex flex-col overflow-hidden rounded-2xl bg-[#0a0e18] border border-white/10 shadow-2xl shadow-black/60"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-accent text-white shrink-0">
                  <Flame className="w-4 h-4" />
                </span>
                <div className="leading-tight min-w-0">
                  <h3 className="font-heading font-bold text-white text-sm truncate">Closefire Assistant</h3>
                  <p className="text-[11px] text-gray-500 truncate">Fire safety product help</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat" className="text-gray-400 hover:text-white shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={
                      m.role === 'user'
                        ? 'max-w-[85%] rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-sm bg-gradient-to-r from-primary to-accent text-white'
                        : 'max-w-[85%] rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm bg-white/[0.06] text-gray-200 border border-white/5 whitespace-pre-wrap'
                    }
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-tl-sm px-4 py-3 bg-white/[0.06] border border-white/5" data-testid="chat-loading">
                    <span className="flex gap-1">
                      {[0, 1, 2].map((d) => (
                        <span
                          key={d}
                          className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                          style={{ animationDelay: `${d * 0.15}s` }}
                        />
                      ))}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Quote / contact shortcut */}
            <div className="px-4 py-2 border-t border-white/10 bg-white/[0.02]">
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1.5 text-xs font-heading font-semibold text-primary hover:text-accent transition-colors"
              >
                Need a quote or bulk order? Contact our team →
              </Link>
            </div>

            {/* Input */}
            <form onSubmit={send} className="flex items-center gap-2 p-3 border-t border-white/10">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about fire safety products…"
                data-testid="chat-input"
                className="flex-1 h-11 rounded-xl bg-white/5 border border-white/10 px-3.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-primary/60"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                aria-label="Send message"
                data-testid="chat-send"
                className="grid place-items-center w-11 h-11 rounded-xl bg-gradient-to-r from-primary to-accent text-white disabled:opacity-40 hover:opacity-95 transition-opacity shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
